// src/components/data-display/TradingViewChart2.jsx

import React, { useState, useEffect, useRef } from 'react';
import { DataTableHeader } from './DataTableHeader';
import { ThemedFlex } from '../../theme/ThemedFlex';
import { Text, useColorMode, useBreakpointValue } from '@chakra-ui/react';

export const TradingViewChart2 = (
    {
        title,
        subtitle,
        icon,
    }
) => {
    const [symbol, setSymbol] = useState('NASDAQ:MSTR/BITSTAMP:BTCUSD');
    const [chartHeight, setChartHeight] = useState('600px');
    const containerRef = useRef(null);
    const resizeObserverRef = useRef(null);

    const { colorMode } = useColorMode();
    const themeColor = colorMode === 'dark' ? 'dark' : 'light';
    
    // Responsive values
    const isMobile = useBreakpointValue({ base: true, md: false });
    const chartPadding = useBreakpointValue({ base: 20, md: 100 });
    const minChartHeight = useBreakpointValue({ base: 300, md: 400 });

    useEffect(() => {
        const calculateHeight = () => {
            if (containerRef.current) {
                // Get viewport height
                const viewportHeight = window.innerHeight;
                // Get the container's position from top
                const containerTop = containerRef.current.getBoundingClientRect().top;
                // Calculate available height with responsive padding
                const availableHeight = viewportHeight - containerTop - chartPadding;
                // Use responsive minimum height
                const finalHeight = Math.max(availableHeight, minChartHeight);
                setChartHeight(`${finalHeight}px`);
            }
        };

        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        
        // Use ResizeObserver for more efficient updates
        if (containerRef.current && window.ResizeObserver) {
            resizeObserverRef.current = new ResizeObserver(calculateHeight);
            resizeObserverRef.current.observe(containerRef.current);
        }
        
        return () => {
            window.removeEventListener('resize', calculateHeight);
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }
        };
    }, [chartPadding, minChartHeight]);

    const containerStyle = {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    };

    const dropdownStyle = {
        marginTop: '7px',
        marginBottom: isMobile ? '10px' : '20px',
        padding: isMobile ? '5px' : '7px',
        borderRadius: '3px',
        fontSize: isMobile ? '11px' : '12px',
        border: '2px solid orange',
        width: isMobile ? '100%' : 'auto',
        maxWidth: isMobile ? '200px' : 'none',
    };

    const handleSelectChange = (e) => {
        setSymbol(e.target.value);
    }

    return (
        <ThemedFlex
            direction="column"
            mb={{ base: "8px", md: "12px" }}
            py={{ base: "12px", md: "18px" }}
            px={{ base: "12px", md: "20px" }}
            flex={1}
            borderRadius={3}
        >
            <div style={containerStyle} ref={containerRef}>
                <DataTableHeader title={title} subtitle={subtitle} icon={icon} />

                <div style={{ 
                    display: 'flex', 
                    alignItems: isMobile ? 'flex-start' : 'center', 
                    justifyContent: 'space-between', 
                    marginBottom: isMobile ? '10px' : '20px',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '10px' : '0'
                }}>
                    <select value={symbol} style={{ ...dropdownStyle, marginBottom: 0 }} onChange={handleSelectChange}>
                    {/* Technology */}
                    <optgroup label="Technology">
                        <option value="NASDAQ:ADBE/BITSTAMP:BTCUSD">ADBE - Adobe vs BTC</option>
                        <option value="NASDAQ:AMD/BITSTAMP:BTCUSD">AMD - AMD vs BTC</option>
                        <option value="NASDAQ:AMZN/BITSTAMP:BTCUSD">AMZN - Amazon vs BTC</option>
                        <option value="NASDAQ:AAPL/BITSTAMP:BTCUSD">AAPL - Apple vs BTC</option>
                        <option value="NYSE:SQ/BITSTAMP:BTCUSD">SQ - Block (Square) vs BTC</option>
                        <option value="NASDAQ:CSCO/BITSTAMP:BTCUSD">CSCO - Cisco Systems vs BTC</option>
                        <option value="NASDAQ:COIN/BITSTAMP:BTCUSD">COIN - Coinbase vs BTC</option>
                        <option value="NYSE:DELL/BITSTAMP:BTCUSD">DELL - Dell Technologies vs BTC</option>
                        <option value="NASDAQ:DOCU/BITSTAMP:BTCUSD">DOCU - DocuSign vs BTC</option>
                        <option value="NASDAQ:EBAY/BITSTAMP:BTCUSD">EBAY - eBay vs BTC</option>
                        <option value="NASDAQ:GOOG/BITSTAMP:BTCUSD">GOOG - Google vs BTC</option>
                        <option value="NYSE:IBM/BITSTAMP:BTCUSD">IBM - IBM vs BTC</option>
                        <option value="NASDAQ:INTC/BITSTAMP:BTCUSD">INTC - Intel vs BTC</option>
                        <option value="NASDAQ:LYFT/BITSTAMP:BTCUSD">LYFT - Lyft vs BTC</option>
                        <option value="NASDAQ:META/BITSTAMP:BTCUSD">META - Meta (Facebook) vs BTC</option>
                        <option value="NASDAQ:MSTR/BITSTAMP:BTCUSD">MSTR - MicroStrategy vs BTC</option>
                        <option value="BATS:MSTY/BITSTAMP:BTCUSD">MSTY - YieldMax MSTR Option Income Strategy ETF vs BTC</option>
                        <option value="NASDAQ:MSFT/BITSTAMP:BTCUSD">MSFT - Microsoft vs BTC</option>
                        <option value="NASDAQ:NFLX/BITSTAMP:BTCUSD">NFLX - Netflix vs BTC</option>
                        <option value="NASDAQ:NVDA/BITSTAMP:BTCUSD">NVDA - Nvidia vs BTC</option>
                        <option value="NASDAQ:PYPL/BITSTAMP:BTCUSD">PYPL - PayPal vs BTC</option>
                        <option value="NASDAQ:HOOD/BITSTAMP:BTCUSD">HOOD - Robinhood vs BTC</option>
                        <option value="NASDAQ:ROKU/BITSTAMP:BTCUSD">ROKU - Roku vs BTC</option>
                        <option value="NYSE:CRM/BITSTAMP:BTCUSD">CRM - Salesforce vs BTC</option>
                        <option value="NYSE:SNAP/BITSTAMP:BTCUSD">SNAP - Snap vs BTC</option>
                        <option value="NASDAQ:TSLA/BITSTAMP:BTCUSD">TSLA - Tesla vs BTC</option>
                        <option value="NYSE:TWTR/BITSTAMP:BTCUSD">TWTR - Twitter vs BTC</option>
                        <option value="NYSE:UBER/BITSTAMP:BTCUSD">UBER - Uber vs BTC</option>
                        <option value="NASDAQ:ZM/BITSTAMP:BTCUSD">ZM - Zoom vs BTC</option>
                    </optgroup>
                    
                    {/* Bitcoin Mining Companies */}
                    <optgroup label="Bitcoin Mining">
                        <option value="NASDAQ:ARBK/BITSTAMP:BTCUSD">ARBK - Argo Blockchain vs BTC</option>
                        <option value="NASDAQ:BTDR/BITSTAMP:BTCUSD">BTDR - Bitdeer vs BTC</option>
                        <option value="NASDAQ:BITF/BITSTAMP:BTCUSD">BITF - Bitfarms vs BTC</option>
                        <option value="NASDAQ:CIFR/BITSTAMP:BTCUSD">CIFR - Cipher Mining vs BTC</option>
                        <option value="NASDAQ:CLSK/BITSTAMP:BTCUSD">CLSK - CleanSpark vs BTC</option>
                        <option value="NASDAQ:HIVE/BITSTAMP:BTCUSD">HIVE - HIVE Blockchain vs BTC</option>
                        <option value="NASDAQ:HUT/BITSTAMP:BTCUSD">HUT - Hut 8 vs BTC</option>
                        <option value="NASDAQ:IREN/BITSTAMP:BTCUSD">IREN - Iris Energy vs BTC</option>
                        <option value="NASDAQ:MARA/BITSTAMP:BTCUSD">MARA - Marathon Digital vs BTC</option>
                        <option value="NASDAQ:RIOT/BITSTAMP:BTCUSD">RIOT - Riot Platforms vs BTC</option>
                        <option value="NASDAQ:WULF/BITSTAMP:BTCUSD">WULF - TeraWulf vs BTC</option>
                    </optgroup>
                    
                    {/* Financial */}
                    <optgroup label="Financial">
                        <option value="NYSE:AXP/BITSTAMP:BTCUSD">AXP - American Express vs BTC</option>
                        <option value="NYSE:BAC/BITSTAMP:BTCUSD">BAC - Bank of America vs BTC</option>
                        <option value="NYSE:BRK.A/BITSTAMP:BTCUSD">BRK.A - Berkshire Hathaway A vs BTC</option>
                        <option value="NYSE:BRK.B/BITSTAMP:BTCUSD">BRK.B - Berkshire Hathaway B vs BTC</option>
                        <option value="NYSE:BLK/BITSTAMP:BTCUSD">BLK - BlackRock vs BTC</option>
                        <option value="NYSE:SCHW/BITSTAMP:BTCUSD">SCHW - Charles Schwab vs BTC</option>
                        <option value="NYSE:C/BITSTAMP:BTCUSD">C - Citigroup vs BTC</option>
                        <option value="NYSE:GS/BITSTAMP:BTCUSD">GS - Goldman Sachs vs BTC</option>
                        <option value="NYSE:JPM/BITSTAMP:BTCUSD">JPM - JPMorgan Chase vs BTC</option>
                        <option value="NYSE:MA/BITSTAMP:BTCUSD">MA - Mastercard vs BTC</option>
                        <option value="NYSE:MS/BITSTAMP:BTCUSD">MS - Morgan Stanley vs BTC</option>
                        <option value="NYSE:PNC/BITSTAMP:BTCUSD">PNC - PNC Financial vs BTC</option>
                        <option value="NYSE:RY/BITSTAMP:BTCUSD">RY - Royal Bank of Canada vs BTC</option>
                        <option value="NYSE:TD/BITSTAMP:BTCUSD">TD - Toronto-Dominion Bank vs BTC</option>
                        <option value="NYSE:TFC/BITSTAMP:BTCUSD">TFC - Truist Financial vs BTC</option>
                        <option value="NYSE:USB/BITSTAMP:BTCUSD">USB - U.S. Bancorp vs BTC</option>
                        <option value="NYSE:V/BITSTAMP:BTCUSD">V - Visa vs BTC</option>
                        <option value="NYSE:WFC/BITSTAMP:BTCUSD">WFC - Wells Fargo vs BTC</option>
                    </optgroup>
                    
                    {/* Healthcare */}
                    <optgroup label="Healthcare">
                        <option value="NYSE:ABBV/BITSTAMP:BTCUSD">ABBV - AbbVie vs BTC</option>
                        <option value="NYSE:ABT/BITSTAMP:BTCUSD">ABT - Abbott Laboratories vs BTC</option>
                        <option value="NASDAQ:AMGN/BITSTAMP:BTCUSD">AMGN - Amgen vs BTC</option>
                        <option value="NASDAQ:BIIB/BITSTAMP:BTCUSD">BIIB - Biogen vs BTC</option>
                        <option value="NYSE:CVS/BITSTAMP:BTCUSD">CVS - CVS Health vs BTC</option>
                        <option value="NYSE:LLY/BITSTAMP:BTCUSD">LLY - Eli Lilly vs BTC</option>
                        <option value="NASDAQ:GILD/BITSTAMP:BTCUSD">GILD - Gilead Sciences vs BTC</option>
                        <option value="NYSE:JNJ/BITSTAMP:BTCUSD">JNJ - Johnson & Johnson vs BTC</option>
                        <option value="NYSE:MDT/BITSTAMP:BTCUSD">MDT - Medtronic vs BTC</option>
                        <option value="NYSE:MRK/BITSTAMP:BTCUSD">MRK - Merck vs BTC</option>
                        <option value="NASDAQ:MRNA/BITSTAMP:BTCUSD">MRNA - Moderna vs BTC</option>
                        <option value="NYSE:PFE/BITSTAMP:BTCUSD">PFE - Pfizer vs BTC</option>
                        <option value="NASDAQ:REGN/BITSTAMP:BTCUSD">REGN - Regeneron vs BTC</option>
                        <option value="NYSE:TMO/BITSTAMP:BTCUSD">TMO - Thermo Fisher Scientific vs BTC</option>
                        <option value="NYSE:UNH/BITSTAMP:BTCUSD">UNH - UnitedHealth Group vs BTC</option>
                    </optgroup>
                    
                    {/* Consumer Goods */}
                    <optgroup label="Consumer Goods">
                        <option value="NYSE:KO/BITSTAMP:BTCUSD">KO - Coca-Cola vs BTC</option>
                        <option value="NYSE:CL/BITSTAMP:BTCUSD">CL - Colgate-Palmolive vs BTC</option>
                        <option value="NASDAQ:COST/BITSTAMP:BTCUSD">COST - Costco vs BTC</option>
                        <option value="NYSE:GIS/BITSTAMP:BTCUSD">GIS - General Mills vs BTC</option>
                        <option value="NYSE:HD/BITSTAMP:BTCUSD">HD - Home Depot vs BTC</option>
                        <option value="NYSE:K/BITSTAMP:BTCUSD">K - Kellogg vs BTC</option>
                        <option value="NYSE:LOW/BITSTAMP:BTCUSD">LOW - Lowe's vs BTC</option>
                        <option value="NYSE:MCD/BITSTAMP:BTCUSD">MCD - McDonald's vs BTC</option>
                        <option value="NASDAQ:MDLZ/BITSTAMP:BTCUSD">MDLZ - Mondelez vs BTC</option>
                        <option value="NYSE:NKE/BITSTAMP:BTCUSD">NKE - Nike vs BTC</option>
                        <option value="NYSE:PG/BITSTAMP:BTCUSD">PG - Procter & Gamble vs BTC</option>
                        <option value="NASDAQ:SBUX/BITSTAMP:BTCUSD">SBUX - Starbucks vs BTC</option>
                        <option value="NYSE:TGT/BITSTAMP:BTCUSD">TGT - Target vs BTC</option>
                        <option value="NYSE:UL/BITSTAMP:BTCUSD">UL - Unilever vs BTC</option>
                        <option value="NYSE:WMT/BITSTAMP:BTCUSD">WMT - Walmart vs BTC</option>
                    </optgroup>
                    
                    {/* Energy */}
                    {/* <optgroup label="Energy">
                        <option value="NYSE:XOM/BITSTAMP:BTCUSD">Exxon Mobil vs BTC</option>
                        <option value="NYSE:CVX/BITSTAMP:BTCUSD">Chevron vs BTC</option>
                        <option value="NYSE:COP/BITSTAMP:BTCUSD">ConocoPhillips vs BTC</option>
                        <option value="NYSE:SLB/BITSTAMP:BTCUSD">Schlumberger vs BTC</option>
                        <option value="NYSE:OXY/BITSTAMP:BTCUSD">Occidental Petroleum vs BTC</option>
                        <option value="NYSE:EOG/BITSTAMP:BTCUSD">EOG Resources vs BTC</option>
                        <option value="NYSE:PSX/BITSTAMP:BTCUSD">Phillips 66 vs BTC</option>
                        <option value="NYSE:VLO/BITSTAMP:BTCUSD">Valero Energy vs BTC</option>
                        <option value="NYSE:MPC/BITSTAMP:BTCUSD">Marathon Petroleum vs BTC</option>
                        <option value="NYSE:KMI/BITSTAMP:BTCUSD">Kinder Morgan vs BTC</option>
                        <option value="NYSE:HAL/BITSTAMP:BTCUSD">Halliburton vs BTC</option>
                    </optgroup>
                     */}
                    {/* Industrial */}
                    <optgroup label="Industrial">
                        <option value="NYSE:MMM/BITSTAMP:BTCUSD">MMM - 3M vs BTC</option>
                        <option value="NYSE:BA/BITSTAMP:BTCUSD">BA - Boeing vs BTC</option>
                        <option value="NYSE:CAT/BITSTAMP:BTCUSD">CAT - Caterpillar vs BTC</option>
                        <option value="NYSE:EMR/BITSTAMP:BTCUSD">EMR - Emerson Electric vs BTC</option>
                        <option value="NYSE:FDX/BITSTAMP:BTCUSD">FDX - FedEx vs BTC</option>
                        <option value="NYSE:GE/BITSTAMP:BTCUSD">GE - General Electric vs BTC</option>
                        <option value="NYSE:DE/BITSTAMP:BTCUSD">DE - John Deere vs BTC</option>
                        <option value="NYSE:LMT/BITSTAMP:BTCUSD">LMT - Lockheed Martin vs BTC</option>
                        <option value="NYSE:NOC/BITSTAMP:BTCUSD">NOC - Northrop Grumman vs BTC</option>
                        <option value="NYSE:RTX/BITSTAMP:BTCUSD">RTX - Raytheon Technologies vs BTC</option>
                        <option value="NYSE:UPS/BITSTAMP:BTCUSD">UPS - UPS vs BTC</option>
                    </optgroup>
                    
                    {/* Entertainment & Media */}
                    <optgroup label="Entertainment & Media">
                        <option value="NASDAQ:ATVI/BITSTAMP:BTCUSD">ATVI - Activision Blizzard vs BTC</option>
                        <option value="NYSE:AMC/BITSTAMP:BTCUSD">AMC - AMC Entertainment vs BTC</option>
                        <option value="NYSE:T/BITSTAMP:BTCUSD">T - AT&T vs BTC</option>
                        <option value="NASDAQ:CHTR/BITSTAMP:BTCUSD">CHTR - Charter Communications vs BTC</option>
                        <option value="NASDAQ:CMCSA/BITSTAMP:BTCUSD">CMCSA - Comcast vs BTC</option>
                        <option value="NASDAQ:EA/BITSTAMP:BTCUSD">EA - Electronic Arts vs BTC</option>
                        <option value="NASDAQ:FOXA/BITSTAMP:BTCUSD">FOXA - Fox Corporation vs BTC</option>
                        <option value="NYSE:GME/BITSTAMP:BTCUSD">GME - GameStop vs BTC</option>
                        <option value="NYSE:NYT/BITSTAMP:BTCUSD">NYT - New York Times vs BTC</option>
                        <option value="NASDAQ:TMUS/BITSTAMP:BTCUSD">TMUS - T-Mobile vs BTC</option>
                        <option value="NASDAQ:TTWO/BITSTAMP:BTCUSD">TTWO - Take-Two Interactive vs BTC</option>
                        <option value="NYSE:VZ/BITSTAMP:BTCUSD">VZ - Verizon vs BTC</option>
                        <option value="NYSE:DIS/BITSTAMP:BTCUSD">DIS - Walt Disney vs BTC</option>
                        <option value="NASDAQ:WBD/BITSTAMP:BTCUSD">WBD - Warner Bros Discovery vs BTC</option>
                    </optgroup>
                    
                    {/* Automotive */}
                    <optgroup label="Automotive">
                        <option value="NYSE:F/BITSTAMP:BTCUSD">F - Ford vs BTC</option>
                        <option value="NYSE:GM/BITSTAMP:BTCUSD">GM - General Motors vs BTC</option>
                        <option value="NYSE:HMC/BITSTAMP:BTCUSD">HMC - Honda vs BTC</option>
                        <option value="NASDAQ:LCID/BITSTAMP:BTCUSD">LCID - Lucid vs BTC</option>
                        <option value="NYSE:NIO/BITSTAMP:BTCUSD">NIO - NIO vs BTC</option>
                        <option value="NASDAQ:RIVN/BITSTAMP:BTCUSD">RIVN - Rivian vs BTC</option>
                        <option value="NYSE:STLA/BITSTAMP:BTCUSD">STLA - Stellantis vs BTC</option>
                        <option value="NYSE:TM/BITSTAMP:BTCUSD">TM - Toyota vs BTC</option>
                        <option value="NYSE:XPEV/BITSTAMP:BTCUSD">XPEV - XPeng vs BTC</option>
                    </optgroup>
                    
                    {/* Real Estate */}
                    <optgroup label="Real Estate">
                        <option value="NYSE:AMT/BITSTAMP:BTCUSD">AMT - American Tower vs BTC</option>
                        <option value="NYSE:AVB/BITSTAMP:BTCUSD">AVB - AvalonBay vs BTC</option>
                        <option value="NYSE:CCI/BITSTAMP:BTCUSD">CCI - Crown Castle vs BTC</option>
                        <option value="NYSE:DLR/BITSTAMP:BTCUSD">DLR - Digital Realty vs BTC</option>
                        <option value="NYSE:EQR/BITSTAMP:BTCUSD">EQR - Equity Residential vs BTC</option>
                        <option value="NYSE:PLD/BITSTAMP:BTCUSD">PLD - Prologis vs BTC</option>
                        <option value="NYSE:PSA/BITSTAMP:BTCUSD">PSA - Public Storage vs BTC</option>
                        <option value="NYSE:O/BITSTAMP:BTCUSD">O - Realty Income vs BTC</option>
                        <option value="NYSE:SPG/BITSTAMP:BTCUSD">SPG - Simon Property vs BTC</option>
                    </optgroup>
                    
                    {/* Materials */}
                    <optgroup label="Materials">
                        <option value="NYSE:APD/BITSTAMP:BTCUSD">APD - Air Products vs BTC</option>
                        <option value="NYSE:DOW/BITSTAMP:BTCUSD">DOW - Dow Inc vs BTC</option>
                        <option value="NYSE:DD/BITSTAMP:BTCUSD">DD - DuPont vs BTC</option>
                        <option value="NYSE:ECL/BITSTAMP:BTCUSD">ECL - Ecolab vs BTC</option>
                        <option value="NYSE:FCX/BITSTAMP:BTCUSD">FCX - Freeport-McMoRan vs BTC</option>
                        <option value="NYSE:NEM/BITSTAMP:BTCUSD">NEM - Newmont vs BTC</option>
                        <option value="NYSE:NUE/BITSTAMP:BTCUSD">NUE - Nucor vs BTC</option>
                        <option value="NYSE:PPG/BITSTAMP:BTCUSD">PPG - PPG Industries vs BTC</option>
                        <option value="NYSE:SHW/BITSTAMP:BTCUSD">SHW - Sherwin-Williams vs BTC</option>
                    </optgroup>
                    
                    {/* ETFs & Commodities */}
                    <optgroup label="ETFs & Commodities">
                        <option value="AMEX:XLE/BITSTAMP:BTCUSD">XLE - Energy Select Sector vs BTC</option>
                        <option value="AMEX:XLF/BITSTAMP:BTCUSD">XLF - Financial Select Sector vs BTC</option>
                        <option value="AMEX:GBTC/BITSTAMP:BTCUSD">GBTC - GBTC vs BTC</option>
                        <option value="NASDAQ:QQQ/BITSTAMP:BTCUSD">QQQ - Invesco QQQ vs BTC</option>
                        <option value="AMEX:SLV/BITSTAMP:BTCUSD">SLV - iShares Silver Trust vs BTC</option>
                        <option value="AMEX:IWM/BITSTAMP:BTCUSD">IWM - iShares Russell 2000 vs BTC</option>
                        <option value="NASDAQ:TLT/BITSTAMP:BTCUSD">TLT - Long Term Treasuries vs BTC</option>
                        <option value="AMEX:DIA/BITSTAMP:BTCUSD">DIA - SPDR Dow Jones vs BTC</option>
                        <option value="AMEX:GLD/BITSTAMP:BTCUSD">GLD - SPDR Gold Trust vs BTC</option>
                        <option value="AMEX:SPY/BITSTAMP:BTCUSD">SPY - SPDR S&P 500 vs BTC</option>
                        <option value="AMEX:XLK/BITSTAMP:BTCUSD">XLK - Technology Select Sector vs BTC</option>
                        <option value="AMEX:USO/BITSTAMP:BTCUSD">USO - United States Oil Fund vs BTC</option>
                        <option value="AMEX:VNQ/BITSTAMP:BTCUSD">VNQ - Vanguard Real Estate vs BTC</option>
                    </optgroup>
                    
                    {/* International */}
                    <optgroup label="International">
                        <option value="NYSE:BABA/BITSTAMP:BTCUSD">BABA - Alibaba vs BTC</option>
                        <option value="NASDAQ:ASML/BITSTAMP:BTCUSD">ASML - ASML Holding vs BTC</option>
                        <option value="NASDAQ:BIDU/BITSTAMP:BTCUSD">BIDU - Baidu vs BTC</option>
                        {/* <option value="NYSE:BP/BITSTAMP:BTCUSD">BP vs BTC</option> */}
                        <option value="NYSE:BTI/BITSTAMP:BTCUSD">BTI - British American Tobacco vs BTC</option>
                        <option value="NYSE:GSK/BITSTAMP:BTCUSD">GSK - GSK vs BTC</option>
                        <option value="NYSE:HSBC/BITSTAMP:BTCUSD">HSBC - HSBC vs BTC</option>
                        <option value="NASDAQ:JD/BITSTAMP:BTCUSD">JD - JD.com vs BTC</option>
                        <option value="NYSE:NVS/BITSTAMP:BTCUSD">NVS - Novartis vs BTC</option>
                        <option value="NASDAQ:PDD/BITSTAMP:BTCUSD">PDD - PDD Holdings vs BTC</option>
                        <option value="NYSE:SAP/BITSTAMP:BTCUSD">SAP - SAP vs BTC</option>
                        {/* <option value="NYSE:SHEL/BITSTAMP:BTCUSD">Shell vs BTC</option> */}
                        <option value="NYSE:SONY/BITSTAMP:BTCUSD">SONY - Sony vs BTC</option>
                        <option value="NYSE:TSM/BITSTAMP:BTCUSD">TSM - Taiwan Semiconductor vs BTC</option>
                        {/* <option value="NYSE:TTE/BITSTAMP:BTCUSD">TotalEnergies vs BTC</option> */}
                    </optgroup>
                    
                    {/* Others */}
                    <optgroup label="Others">
                        <option value="NYSE:AIG/BITSTAMP:BTCUSD">AIG - AIG vs BTC</option>
                        <option value="NYSE:ALL/BITSTAMP:BTCUSD">ALL - Allstate vs BTC</option>
                        <option value="NYSE:MO/BITSTAMP:BTCUSD">MO - Altria Group vs BTC</option>
                        <option value="NYSE:CB/BITSTAMP:BTCUSD">CB - Chubb vs BTC</option>
                        <option value="NYSE:LYB/BITSTAMP:BTCUSD">LYB - LyondellBasell vs BTC</option>
                        <option value="NYSE:MET/BITSTAMP:BTCUSD">MET - MetLife vs BTC</option>
                        <option value="NASDAQ:NDAQ/BITSTAMP:BTCUSD">NDAQ - Nasdaq vs BTC</option>
                        <option value="NYSE:PM/BITSTAMP:BTCUSD">PM - Philip Morris vs BTC</option>
                        <option value="NYSE:PGR/BITSTAMP:BTCUSD">PGR - Progressive vs BTC</option>
                        <option value="NYSE:PRU/BITSTAMP:BTCUSD">PRU - Prudential vs BTC</option>
                        <option value="NYSE:TRV/BITSTAMP:BTCUSD">TRV - Travelers Companies vs BTC</option>
                        <option value="NASDAQ:WBA/BITSTAMP:BTCUSD">WBA - Walgreens Boots Alliance vs BTC</option>
                    </optgroup>
                    </select>
                    
                    <Text
                        fontSize={{ base: '10px', md: '12px' }}
                        color={'gray.700'}
                        ml={{ base: 0, md: 3 }}
                        textAlign={{ base: 'left', md: 'left' }}
                        maxW={{ base: '100%', md: 'none' }}
                    >
                        Select a stock and see the performance vs Bitcoin over time using a 1:1 ratio.
                        {!isMobile && ' Ex: If a stock = 1 BTC, then the ratio is 1.'}
                    </Text>
                </div>

                <div style={{ flex: 1, minHeight: 0 }}>
                    <iframe
                        title="TradingView Chart"
                        src={`https://www.tradingview.com/widgetembed/?frameElementId=tradingview_7882b&symbol=${symbol}&interval=W&symboledit=0&saveimage=1&toolbarbg=f1f3f6&studies=%5B%22BollingerBands%40tv-basicstudies%22%5D&theme=${themeColor}&style=2&timezone=Etc%2FUTC&withdateranges=1&hideideas=1&studies_overrides=%7B%7D&overrides=%7B%7D&enabled_features=%5B%5D&disabled_features=%5B%5D&locale=en&hide_volume=1&utm_source=localhost&utm_medium=widget&utm_campaign=chart&utm_term=${symbol}`}
                        width="100%"
                        height={chartHeight}
                        frameBorder="0"
                        allowtransparency="true"
                        scrolling="no"
                        allowFullScreen=""
                    ></iframe>
                    <Text
                        mt={{ base: 1, md: 2 }}
                        fontSize={{ base: '9px', md: '10px' }}
                        color={'gray.700'}
                        textAlign="center"
                    >
                        Educational purposes only. Not for financial decisions.
                    </Text>
                </div>
            </div>
        </ThemedFlex>
    );
};

